/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {error} from '@ion/core/src/logger/logger';
import {User} from '@ion/core/src/types/entities';
import {decode} from '@ion/core/src/utils/Cipher';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {hydrateCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {
  APPLICATION_INITIALIZATION_COMPLETE,
  APPLICATION_INITIALIZATION_ERROR
} from '@/store/actions/initialization';
import {
  LOGIN_FAILED,
  LOGIN_REQUEST,
  LOGIN_SUCCEEDED,
  LOGOUT_FAILED,
  LOGOUT_REQUEST,
  MFA_PENDING,
  PERSIST_CREDENTIALS
} from '@/store/actions/login';
import {ApplicationKeys} from '@/types/application';
import {getLoginErrorMessage} from '@/utils/login-utils';

/**
 * Provides the default credentials persisted to localStorage via the remember
 * me feature.
 *
 * @property emptyCredentials
 */
export const emptyCredentials = {
  defaultAccountId : null,
  rememberMe       : false,
  credentials      : {
    username : '',
    password : ''
  }
};

/**
 * @private
 */
const getPersisted = () => {
  const item = safeJSONParse(
    JSON.stringify(localPersistence(ApplicationKeys.CTCAdmin, 'login', null) || emptyCredentials)
  );
  const {password} = item?.credentials;
  if (password) {
    try {
      item.credentials.password = decode(password);
    } catch (err) {
      error(err.toString());
    }
  }
  return item;
};

/**
 * Provides a utility which retrieves the users persisted credentials from
 * localStorage (if available), Otherwise, returns the default emptyCredentials.
 *
 * @method persisted
 */
const persisted = getPersisted();
hydrateCurrentUser(persisted.credentials);

// defines the shape of this reducers state ...
export interface LoginState {
  isAuthenticating: boolean;
  authenticated: boolean;
  isServiceError: boolean;
  isInvalid: boolean;
  persistCredentials: boolean;
  hasPreviewFeature: boolean;
  isForbidden: boolean;
  loginErrorMessage?: string;
  user: User;
}

// defines the initial state for the reducer ...
const initialState: LoginState = {
  isAuthenticating   : false,
  authenticated      : false,
  isServiceError     : false,
  isInvalid          : false,
  loginErrorMessage  : null,
  hasPreviewFeature  : false,
  isForbidden        : false,
  persistCredentials : persisted.rememberMe,
  user               : persisted.credentials
};

// defines the reducers ...
const reducers = {
  [LOGIN_REQUEST] : (draft: LoginState) => {
    draft.loginErrorMessage = null;
    draft.isAuthenticating = true;
    draft.isServiceError = draft.isInvalid = draft.authenticated = draft.isForbidden = false;
  },
  [LOGIN_SUCCEEDED] : (draft: LoginState, {user}) => {
    draft.isAuthenticating = draft.isServiceError = draft.isInvalid = draft.isForbidden = false;
    draft.user = user;
  },
  [LOGIN_FAILED] : (draft: LoginState, {error}) => {
    draft.loginErrorMessage = getLoginErrorMessage(error?.errorCode);
    draft.isInvalid = error?.isUnauthorizedError;
    draft.isServiceError = !error?.isUnauthorizedError;
    draft.authenticated = draft.isAuthenticating = draft.isForbidden = false;
  },
  [MFA_PENDING] : (draft: LoginState) => {
    draft.isAuthenticating = draft.isServiceError = draft.isInvalid = draft.isForbidden = false;
  },
  [PERSIST_CREDENTIALS] : (draft: LoginState, {persist}) => {
    draft.persistCredentials = persist;
  },
  [LOGOUT_REQUEST] : (draft: LoginState) => {
    draft.isAuthenticating = true;
  },
  [LOGOUT_FAILED] : (draft: LoginState) => {
    draft.isAuthenticating = false;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (draft: LoginState) => {
    // [ION-APP TODO]: Set hasPreviewFeature based on user role feature permissions ...
    draft.hasPreviewFeature = true;
    draft.authenticated     = true;
    draft.isForbidden       = false;
  },
  [APPLICATION_INITIALIZATION_ERROR] : (draft: LoginState, {isForbidden}) => {
    draft.isForbidden = isForbidden;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: LoginState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
